import React, { useContext, useRef, useState } from 'react';
import Modal from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link/link';
import { ProofOfDeliveryDetails } from 'src/stores/ProofOfDeliveryDetails';
import PackageDeliveryDetailsContext, { SignatureDetails, RecipientInformation } from 'src/stores/PackageDeliveryDetails';
import {
  INCORRECT_ZIP_CODE,
  STATES,
  DISPLAY_PRIORITY,
} from 'src/utils/Constants';
import { POD_METRICS, SOD_METRICS, RECIPIENT_METRICS } from 'src/utils/DeliveryMetrics';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Column from '@amzn/meridian/column';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/helpers';
import { HOMEPAGE_MESSAGE_BUNDLE_NAME } from 'src/utils/Constants';
import { logger } from 'src/logger';
import { trimZipCode } from 'src/utils/ZipCodeUtils';
import { publishSingleMetricCount } from 'src/metrics';
import { sanitizeText } from 'src/utils/SecurityUtils';
import { getDropOffLocation, getRecipientMessage, DropOffLocation, shouldShowRecipientInformation } from 'src/utils/DropOffLocationUtils';

interface ZipCodeModalProps {
  trackingNumber: string;
  shippingCountry?: string | undefined;
  hasPhotoOnDelivery?: boolean;
  hasSignatureOnDelivery?: boolean;
  hasRecipientInformation?: boolean;
  packageDropOffLocation?: string;
}

const isImageRenderable = (img: { imageType: string }): boolean => !!img.imageType;

const ZipCodeModal: React.FC<ZipCodeModalProps> = ({
  trackingNumber,
  shippingCountry,
  hasPhotoOnDelivery = false,
  hasSignatureOnDelivery = false,
  hasRecipientInformation = false,
  packageDropOffLocation
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [zipCode, setZipCode] = useState<string>('');
  const [proofOfDeliveryDetails, setProofOfDeliveryDetails] =
    useState<ProofOfDeliveryDetails | null>(null);
  const [signatureDetails, setSignatureDetails] = useState<SignatureDetails | null>(null);
  const [recipientInformation, setRecipientInformation] = useState<RecipientInformation | null>(null);
  const [error, setError] = useState<string | undefined>('');
  const [zipCodeError, setZipCodeError] = useState<string | undefined>('');
  const podMetricEmitted = useRef(false);
  const podFailureCount = useRef(0);
  const podImageCount = useRef(0);
  const [bundle] = useBundle(HOMEPAGE_MESSAGE_BUNDLE_NAME);
  const packageDeliveryDetails = useContext(PackageDeliveryDetailsContext);
  const handleOpen = (): void => {
    const flow = hasSignatureOnDelivery ? 'signature on delivery' : 
                 hasRecipientInformation ? 'recipient information' : 'photo on delivery';
    void logger.info(`ZipCode modal opened for tracking number ${trackingNumber} - flow: ${flow}`);
    setOpen(true);
  };
  const handleClose = (): void => {
    void logger.info(`ZipCode modal closed for tracking number ${trackingNumber}`);
    setOpen(false);
    setZipCode('');
    setError('');
    setZipCodeError('');
  };
  const flowName = hasSignatureOnDelivery ? 'SOD' : hasRecipientInformation ? 'Recipient' : 'POD';
  const flowMetrics = hasSignatureOnDelivery ? SOD_METRICS : hasRecipientInformation ? RECIPIENT_METRICS : POD_METRICS;
  const genericZipCodeRegex = /^[A-Z0-9\s-]{2,10}([A-Z]{2})?$/i;

  // Don't render anything if no delivery details are available
  if (!hasPhotoOnDelivery && !hasSignatureOnDelivery && !hasRecipientInformation) {
    return null;
  }

  const getLinkText = (): string | undefined => {
    if (hasSignatureOnDelivery) {
      return t(bundle, 'swiship_view_signature_details');
    } else if (hasPhotoOnDelivery && hasRecipientInformation) {
      // When both POD and recipient are available, check drop-off location priority
      const showRecipient = shouldShowRecipientInformation(packageDropOffLocation, true);
      return showRecipient 
        ? t(bundle, 'swiship_view_recipient_information')
        : t(bundle, 'swiship_view_delivery_photo');
    } else if (hasRecipientInformation) {
      return t(bundle, 'swiship_view_recipient_information');
    } else {
      return t(bundle, 'swiship_view_delivery_photo');
    }
  };

  const getModalTitle = (): string | undefined => {
    if (hasSignatureOnDelivery) {
      return t(bundle, 'swiship_signature_details');
    } else if (hasRecipientInformation) {
      return t(bundle, 'swiship_recipient_information');
    } else {
      return t(bundle, 'swiship_delivery_photo');
    }
  };

  const handleZipCodeChange = (value: string): void => {
    setError('');
    setZipCode(value); // Don't trim - preserve spaces for validation

    if (value.length >= 3) {
      if (genericZipCodeRegex.test(value)) {
        setZipCodeError('');
      } else {
        setZipCodeError(t(bundle, 'swiship_invalid_zip_code_format'));
      }
    } else {
      setZipCodeError('');
    }
  };

  const handleError = (errorMessage: string | undefined): void => {
    void logger.error(`Delivery error displayed to user: trackingId=${trackingNumber}, flow=${flowName}, message=${errorMessage}`);
    setError(errorMessage);
    setProofOfDeliveryDetails(null);
  };

  const handlePODDataReceived = (): void => {
    const podObject = packageDeliveryDetails.getProofOfDeliveryDetails;
    const images = podObject?.getImages;
    if (!Array.isArray(images) || images.length === 0) {
      void logger.error(`POD empty response: trackingId=${trackingNumber}, imageCount=0`);
      publishSingleMetricCount(POD_METRICS.NO_DATA_RECEIVED);
      handleError(t(bundle, 'swiship_something_went_wrong'));
      return;
    }
    publishSingleMetricCount(POD_METRICS.DATA_RECEIVED);
    const renderableImages = images.filter(isImageRenderable);
    podFailureCount.current = 0;
    podImageCount.current = renderableImages.length;
    if (renderableImages.length === 0) {
      podMetricEmitted.current = true;
      publishSingleMetricCount(POD_METRICS.DISPLAY_FAILURE);
    } else {
      podMetricEmitted.current = false;
    }
    setProofOfDeliveryDetails(podObject);
    setError('');
  };

  const handleSODDataReceived = (): void => {
    const sigDetails = packageDeliveryDetails.getSignatureDetails;
    if (!sigDetails) {
      void logger.error(`SOD empty response: trackingId=${trackingNumber}`);
      publishSingleMetricCount(SOD_METRICS.NO_DATA_RECEIVED);
      handleError(t(bundle, 'swiship_something_went_wrong'));
      return;
    }
    publishSingleMetricCount(SOD_METRICS.DATA_RECEIVED);
    setError('');
    setProofOfDeliveryDetails(null);
    setSignatureDetails(sigDetails);
    packageDeliveryDetails.setLoadingState(STATES.DONE);
  };

  const handleRecipientDataReceived = (): void => {
    const recipientInfo = packageDeliveryDetails.getRecipientInformation;
    if (!recipientInfo || (!recipientInfo.recipientName && !recipientInfo.recipientDoorNumber)) {
      void logger.error(`Recipient empty response: trackingId=${trackingNumber}`);
      publishSingleMetricCount(RECIPIENT_METRICS.NO_DATA_RECEIVED);
      handleError(t(bundle, 'swiship_something_went_wrong'));
      return;
    }
    publishSingleMetricCount(RECIPIENT_METRICS.DATA_RECEIVED);
    setError('');
    setProofOfDeliveryDetails(null);
    setSignatureDetails(null);
    setRecipientInformation(recipientInfo);
    packageDeliveryDetails.setLoadingState(STATES.DONE);
  };

  const determineDisplayPriority = (hasSOD: boolean, hasPOD: boolean, hasRecipient: boolean, location?: string) => {
    if (hasSOD) return DISPLAY_PRIORITY.SIGNATURE;
    if (hasPOD && hasRecipient) {
      return shouldShowRecipientInformation(location, true) ? DISPLAY_PRIORITY.RECIPIENT : DISPLAY_PRIORITY.PHOTO;
    }
    if (hasRecipient && shouldShowRecipientInformation(location, false)) return DISPLAY_PRIORITY.RECIPIENT;
    if (hasPOD) return DISPLAY_PRIORITY.PHOTO;
    return null;
  };

  const handleSubmit = async (): Promise<void> => {
    if (zipCodeError) {
      return; // Don't submit if there's a zip code error
    }
    
    setError('');
    
    try {
      if (!trackingNumber) {
        handleError(t(bundle, 'swiship_invalid_tracking_id'));
        return;
      }

      // Validate zipcode is not empty or whitespace-only before making API call
      if (!zipCode || zipCode.trim() === '') {
        setZipCodeError(t(bundle, 'swiship_invalid_zip_code_format'));
        return;
      }

      publishSingleMetricCount('ZipCodeModalSubmit');
      packageDeliveryDetails.setLoadingState(STATES.PENDING);
      const trimmedZipCode = trimZipCode(zipCode, shippingCountry);
      
      void logger.info(`Delivery API request: trackingId=${trackingNumber}, userInputZipCode="${zipCode}", trimmedZipCode="${trimmedZipCode}", country=${shippingCountry}, isPOD=${hasPhotoOnDelivery}, isSOD=${hasSignatureOnDelivery}, isRecipient=${hasRecipientInformation}`);
      
      await packageDeliveryDetails.fetchPackageDeliveryDetails(
        trackingNumber, 
        trimmedZipCode,
        hasPhotoOnDelivery,
        hasSignatureOnDelivery,
        hasRecipientInformation
      );

      // Handle API response
      const errorMessage = packageDeliveryDetails.getErrorMessage;
      if (errorMessage) {
        void logger.warn(`Delivery API error: trackingId=${trackingNumber}, flow=${flowName}, error=${packageDeliveryDetails.getErrorMessage}`);
        packageDeliveryDetails.setLoadingState(STATES.ERROR);
        if (errorMessage === INCORRECT_ZIP_CODE) {
          publishSingleMetricCount(flowMetrics.ZIPCODE_MISMATCH);
          handleError(t(bundle, 'swiship_invalid_zip_code'));
        } else {
          publishSingleMetricCount(flowMetrics.SERVER_ERROR);
          handleError(t(bundle, 'swiship_something_went_wrong'));
        }
      } else {
        // Determine display priority and handle accordingly
        const dropOffLocation = packageDeliveryDetails.getPackageDropOffLocation;
        const priority = determineDisplayPriority(
          hasSignatureOnDelivery && !!packageDeliveryDetails?.getSignatureDetails,
          hasPhotoOnDelivery && !!packageDeliveryDetails?.getProofOfDeliveryDetails,
          hasRecipientInformation && !!packageDeliveryDetails?.getRecipientInformation,
          dropOffLocation
        );

        switch (priority) {
          case DISPLAY_PRIORITY.SIGNATURE:
            handleSODDataReceived();
            break;
          case DISPLAY_PRIORITY.PHOTO:
            handlePODDataReceived();
            break;
          case DISPLAY_PRIORITY.RECIPIENT:
            handleRecipientDataReceived();
            break;
          default:
            packageDeliveryDetails.setLoadingState(STATES.ERROR);
            publishSingleMetricCount(flowMetrics.NO_DATA_RECEIVED);
            handleError(t(bundle, 'swiship_no_delivery_details'));
        }
      }
    } catch (err: any) {
      packageDeliveryDetails.setLoadingState(STATES.ERROR);
      publishSingleMetricCount(flowMetrics.SERVER_ERROR);
      handleError(STATES.ERROR);
      await logger.error(`Unhandled exception during ${flowName} submission: trackingId=${trackingNumber}`, err);
    }
  };

  const hasPhotoData = (): boolean => {
    return hasPhotoOnDelivery && 
           proofOfDeliveryDetails !== null && 
           proofOfDeliveryDetails.getImages.length > 0;
  };

  const hasSignatureData = (): boolean => {
    return hasSignatureOnDelivery && signatureDetails !== null;
  };

  const hasRecipientData = (): boolean => {
    const dropOffLocation = packageDeliveryDetails?.getPackageDropOffLocation;
    return recipientInformation !== null && !!dropOffLocation && 
           shouldShowRecipientInformation(dropOffLocation, hasPhotoOnDelivery);
  };

  const hasAnyDeliveryData = (): boolean => {
    return hasPhotoData() || hasSignatureData() || hasRecipientData();
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
    if (event.key === 'Enter') {
      await handleSubmit();
    }
  };

  return (
    <>
      <Link 
        onClick={handleOpen} 
        type="primary" 
        data-id={hasSignatureOnDelivery ? "view-signature-details-link" : "view-delivery-photo-link"}
      >
        {getLinkText()}
      </Link>
      <Modal
        title={getModalTitle()}
        open={open}
        onClose={handleClose}
        scrollContainer="viewport"
        closeLabel={t(bundle, 'swiship_close_button')}
        aria-describedby="modal-description"
        width={500}
        data-id="delivery-photo-modal"
      >
        {!error && hasAnyDeliveryData() ? (
          <>
            {hasSignatureData() && (
              <div>
                <Text type="b300">{t(bundle, 'swiship_signed_by_label')}</Text>
                <Text type="b400">{sanitizeText(signatureDetails?.signedBy)}</Text>
              </div>
            )}
            {hasRecipientData() && (
              <div>
                <Text type="b400">
                  {(() => {
                    const name = recipientInformation?.recipientName;
                    const doorNumber = recipientInformation?.recipientDoorNumber;
                    const packageDropOffLocation = packageDeliveryDetails?.getPackageDropOffLocation;
                    
                    const messageKey = packageDropOffLocation ? getRecipientMessage(packageDropOffLocation, name, doorNumber) : null;
                    
                    return messageKey 
                      ? t(bundle, messageKey, { 
                          name: name ? sanitizeText(name) : '',
                          doorNumber: doorNumber ? sanitizeText(doorNumber) : ''
                        })
                      : t(bundle, 'swiship_no_delivery_details');
                  })()}
                </Text>
                <div>&nbsp;</div>
              </div>
            )}
            {hasPhotoData() && proofOfDeliveryDetails?.getImages.map((proof, index) => (
                <div key={index}>
                  {isImageRenderable(proof) ? (
                      <>
                        <img
                            width="100%"
                            height="100%"
                            src={proof.imageUrl}
                            alt={t(bundle, 'swiship_delivery_proof')}
                            className="photo-on-delivery-image"
                            onLoad={() => {
                              if (!podMetricEmitted.current) {
                                podMetricEmitted.current = true;
                                publishSingleMetricCount(POD_METRICS.DISPLAY_SUCCESS);
                              }
                            }}
                            onError={() => {
                              void logger.error(`POD image render failure: trackingId=${trackingNumber}, imageIndex=${index}`);
                              podFailureCount.current += 1;
                              if (!podMetricEmitted.current && podFailureCount.current >= podImageCount.current) {
                                podMetricEmitted.current = true;
                                publishSingleMetricCount(POD_METRICS.DISPLAY_FAILURE);
                              }
                            }}
                        />
                      </>
                  ) : (
                      <Text>{t(bundle, 'swiship_something_went_wrong')}</Text>
                  )}
                </div>
            ))}
          </>
        ) : (
          <>
            {packageDeliveryDetails.getLoadingState === STATES.PENDING ? (
              <Column alignmentHorizontal="center">
                <Loader data-testid="loader"></Loader>
              </Column>
            ) : (
              <>
                <Text>{t(bundle, hasSignatureOnDelivery ? 'swiship_enter_zip_code_label_sod' : 
                              hasRecipientInformation ? 'swiship_enter_zip_code_label_recipient' : 
                              'swiship_enter_zip_code_label_pod')}</Text>
                <Column spacingInset="200 none">
                  <Input
                    id="zip-code"
                    data-testid="zip-code"
                    type="text"
                    placeholder={t(bundle, 'swiship_zip_code_placeholder')}
                    value={zipCode}
                    onChange={handleZipCodeChange}
                    onKeyDown={handleKeyDown}
                  />
                </Column>
                {zipCodeError && (
                  <Text color="error" className="zipcode-error">
                    {zipCodeError}
                  </Text>
                )}
                {error && (
                  <Text color="error">
                    {error === STATES.ERROR
                      ? t(bundle, 'swiship_something_went_wrong')
                      : error}
                  </Text>
                )}
                <br />
                <Button onClick={handleSubmit} id="delivery-photo-submit">
                  {t(bundle, 'swiship_submit_button')}
                </Button>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default ZipCodeModal;
